@let comments = comments$ | async;
@let top3 = top3$ | async;
@let stravaSections = stravaSections$ | async;
@let sameContributor = sameContributor$ | async;
@let recommendeds = recommended$ | async;

@defer (when information) {
  <div
    class="watch-background-img"
    [ngStyle]="{ '--bg-img': backgroundImage }"></div>
} @placeholder {
  <div class="watch-background-img"></div>
}

<header id="page-header">
  <kin-circular-icon-btn
    iconColor="white"
    id="back-btn"
    color="'black-500'"
    [backgroundColor]="'transparent'"
    (onClick)="location.back()"
    icon="arrow_left"
    translate>
    {{ 'browse.search.toolbar.back' | translate }}
  </kin-circular-icon-btn>
  @defer (when information) {
    <kin-circle-icon-country
      [icon]="information.sport"
      [country]="null"
      [iconOnly]="true" />
  } @placeholder {
    <kin-circle-icon-country [iconOnly]="true" [country]="null" />
  }
  @if (information) {
    <h1>{{ information.name }}</h1>
  } @else {
    <kin-skeleton height="32" width="300" unit="px" />
  }
</header>

<ng-content></ng-content>

<div class="divider"></div>

@if (isEditable && checkPermission(client, information)) {
  <app-edit-scenic
    [videoId]="information.id"
    [editType]="information.type"
    [information]="information"
    (cancelClick)="toggleEditMode()"
    (postChanges)="editPost($event)"
    [loading$]="editLoading$" />
}

<div class="content">
  <div class="left">
    <div class="bloc detail">
      <kin-header-block
        [label]="'settings.myAccount.Description' | translate | titlecase"
        [viewMoreDisplay]="false" />
      @defer (when information) {
        <kin-watch-actions-block
          [class.hideMore]="
            information.type === 'mapmode' &&
            (!client?.is_admin || client?.id !== information.author.id)
          "
          [class.hideDownloadGpx]="
            information.type === 'scenic' &&
            (!client?.is_admin || client?.id !== information.author.id)
          "
          [information]="information"
          [user]="client"
          (onDownloadGpxClick)="handleDownloadGPX()"
          (onAddPlaylistClick)="openModal('playlist')"
          (onShareClick)="openModal('share')"
          (onIntegrateClick)="openModal('integrate')"
          (onLikesCounterClick)="openModal('likes')"
          (onLikesIconClick)="handleLikeClick()"
          (onCommentsCounterClick)="openModal('comments')"
          (onReportClick)="openModal('report')"
          (onEditClick)="toggleEditMode()"
          (onEditAdminClick)="toggleEditAdminMode()" />
        <kin-watch-description-block
          [informations$]="toObservable(information)"
          [authorLink]="authorProfileLink"
          (authorClick)="navigate('author', $event.userId)">
        </kin-watch-description-block>
      } @placeholder {
        <kin-watch-actions-block />
        <kin-watch-description-block />
      }
    </div>

    <div class="bloc comments">
      <kin-header-block
        [label]="'dashboard.popup-comments.comments' | translate | titlecase"
        [viewMoreDisplay]="comments?.length > 0"
        [viewMoreLabel]="'watch.content.ranking.view_all_btn' | translate"
        (viewMoreClick)="openModal('comments')" />
      @if (comments?.length > 0) {
        <div class="comments-container">
          @for (comment of comments; track $index) {
            @if ($index < 3) {
              <kin-comment-card
                [extended]="true"
                [comment]="comment"
                (userClick)="navigate('author', $event)"
                (commentClick)="openModal('comments')" />
            }
          }
        </div>
      } @else {
        <kin-empty-comment-card
          [logged]="!!client"
          (newComment)="openModal('comments')"
          (login)="navigate('login')" />
      }
    </div>
  </div>

  <div class="right">
    <div class="bloc">
      @if (checkPermission(client, information)) {
        @defer (when information && checkPermission(client, information)) {
          <kin-header-block
            size="medium"
            [label]="'watch.content.thumbnail.title' | translate | titlecase"
            [viewMoreDisplay]="false" />
          <div class="thumbnails-preview">
            <img
              [src]="getThumbnailUrl(information.thumbnails)"
              alt="thumbnail preview" />
          </div>
        } @placeholder {
          <kin-skeleton height="24" unit="px" [extended]="true" />
          <kin-skeleton height="180" unit="px" [extended]="true" />
        }
      }
    </div>
    @if (showRanking) {
      <div class="bloc ranking">
        <kin-header-block
          size="medium"
          viewMoreDisplay="true"
          [label]="'watch.content.ranking.title' | translate"
          [viewMoreLabel]="'watch.content.ranking.view_all_btn' | translate"
          (viewMoreClick)="openModal('ranking', top3)" />
        <kin-ranking-block
          [top3]="top3"
          [user]="client"
          (podiumClick)="openModal('ranking', top3)" />
      </div>
    }
    @if (sameContributor) {
      <div class="bloc same-contributor">
        <kin-header-block
          [label]="'watch.common.same_contributor' | translate"
          [viewMoreDisplay]="sameContributor"
          [viewMoreLabel]="'watch.content.ranking.view_all_btn' | translate"
          (viewMoreClick)="openModal('sameContributor', sameContributor)" />
        <div class="same-contributor-wrapper">
          @if (sameContributor) {
            @for (sameContributor of sameContributor; track $index) {
              @if ($index < 4) {
                <kin-scenic-card
                  (onAuthorClick)="navigate('author', $event)"
                  (onCardClick)="navigate('watch', $event)"
                  (onFavoriteClick)="
                    handleFavoriteClick(sameContributor.is_favorite)
                  "
                  [information]="sameContributor"
                  size="small" />
              }
            }
          } @else {
            <div class="empty-card">
              <p>{{ 'workout.contributor_has_not_workout' | translate }}</p>
              <kin-btn [fill]="false" color="secondary">
                {{
                  'watch.workout.create_your_workout' | translate | uppercase
                }}
              </kin-btn>
            </div>
          }
        </div>
      </div>
    }
    @if (stravaSections$) {
      <div class="bloc strava-sections">
        <kin-header-block
          [label]="'watch.strava_sections.title' | translate | titlecase"
          [viewMoreDisplay]="sameContributor$ | async"
          [viewMoreLabel]="'watch.content.ranking.view_all_btn' | translate"
          (viewMoreClick)="openModal('strava', stravaSections)" />
        <kin-strava-sections-block
          [sections$]="toObservable(stravaSections$ | async | slice: 0 : 3)" />
      </div>
    }
  </div>

  @if (recommendeds && recommendeds.length > 0) {
    <div class="bloc recommended">
      <kin-header-block
        [label]="'browse.recommended.title' | translate"
        [viewMoreDisplay]="true"
        [viewMoreLabel]="'watch.content.ranking.view_all_btn' | translate"
        (viewMoreClick)="openModal('recommended', recommendeds)" />
      <kin-horizontal-scroller>
        @for (recommended of recommendeds; track $index) {
          <kin-scenic-card
            [ngStyle]="{ 'margin-right': '12px' }"
            [information]="recommended"
            [details]="true"
            [condensed]="false"
            [animate]="true"
            (onCardClick)="navigate('watch', recommended.id)"
            (onAuthorClick)="navigate('author', recommended.author.id)"
            (onFavoriteClick)="handleFavoriteClick(recommended.is_favorite)"
            size="small" />
        }
      </kin-horizontal-scroller>
    </div>
  }
</div>
